import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function ContentManager() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('all'); // all, published, draft
  const [editingBlocks, setEditingBlocks] = useState({});
  const [selectedForPublish, setSelectedForPublish] = useState([]);

  useEffect(() => {
    loadBlocks();
  }, [mode]);

  const loadBlocks = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/content-blocks/?mode=${mode}`);
      setBlocks(response);
      
      // Initialize editing state
      const initialEditing = {};
      response.forEach(block => {
        initialEditing[block._id] = { ...block };
      });
      setEditingBlocks(initialEditing);
    } catch (error) {
      console.error('Failed to load blocks:', error);
      toast.error('Грешка при зареждане на съдържанието');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (blockId, newValue) => {
    setEditingBlocks(prev => ({
      ...prev,
      [blockId]: {
        ...prev[blockId],
        value: newValue
      }
    }));
  };

  const handleSaveBlock = async (blockId) => {
    try {
      setSaving(true);
      const block = editingBlocks[blockId];
      
      await apiClient.put(`/api/content-blocks/${blockId}`, {
        key: block.key,
        value: block.value,
        section: block.section,
        status: block.status || 'draft'
      });
      
      toast.success('Съдържанието е запазено');
      loadBlocks();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Грешка при запазване');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishSelected = async () => {
    if (selectedForPublish.length === 0) {
      toast.error('Изберете поне един блок за публикуване');
      return;
    }

    try {
      setSaving(true);
      
      // Get keys from selected blocks
      const keys = selectedForPublish
        .map(id => blocks.find(b => b._id === id)?.key)
        .filter(Boolean);
      
      await apiClient.post('/api/content-blocks/publish', keys);
      
      toast.success(`${keys.length} блока са публикувани успешно`);
      setSelectedForPublish([]);
      loadBlocks();
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error('Грешка при публикуване');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublishSelected = async () => {
    if (selectedForPublish.length === 0) {
      toast.error('Изберете поне един блок');
      return;
    }

    try {
      setSaving(true);
      
      const keys = selectedForPublish
        .map(id => blocks.find(b => b._id === id)?.key)
        .filter(Boolean);
      
      await apiClient.post('/api/content-blocks/unpublish', keys);
      
      toast.success(`${keys.length} блока са върнати в чернова`);
      setSelectedForPublish([]);
      loadBlocks();
    } catch (error) {
      console.error('Unpublish failed:', error);
      toast.error('Грешка при промяна на статуса');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelection = (blockId) => {
    setSelectedForPublish(prev => 
      prev.includes(blockId) 
        ? prev.filter(id => id !== blockId)
        : [...prev, blockId]
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'published') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-green-900/30 text-green-400 rounded text-xs">
          <CheckCircle className="w-3 h-3" />
          Публикувано
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 bg-yellow-900/30 text-yellow-400 rounded text-xs">
        <AlertCircle className="w-3 h-3" />
        Чернова
      </span>
    );
  };

  const groupedBlocks = blocks.reduce((acc, block) => {
    if (!acc[block.section]) {
      acc[block.section] = [];
    }
    acc[block.section].push(block);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-playfair font-bold">Управление на съдържание</h1>
          <p className="text-neutral-400 mt-1">Редактирайте и публикувайте съдържанието на сайта</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadBlocks}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Обнови
          </Button>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center justify-between bg-primary-charcoal p-4 rounded-lg border border-neutral-700">
        <div className="flex gap-2">
          <button
            onClick={() => setMode('all')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              mode === 'all' 
                ? 'bg-accent-gold text-primary-black' 
                : 'bg-primary-black text-neutral-300 hover:bg-neutral-800'
            }`}
          >
            Всички ({blocks.length})
          </button>
          <button
            onClick={() => setMode('published')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              mode === 'published' 
                ? 'bg-accent-gold text-primary-black' 
                : 'bg-primary-black text-neutral-300 hover:bg-neutral-800'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-1" />
            Публикувани
          </button>
          <button
            onClick={() => setMode('draft')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              mode === 'draft' 
                ? 'bg-accent-gold text-primary-black' 
                : 'bg-primary-black text-neutral-300 hover:bg-neutral-800'
            }`}
          >
            <EyeOff className="w-4 h-4 inline mr-1" />
            Черновки
          </button>
        </div>

        {selectedForPublish.length > 0 && (
          <div className="flex gap-2">
            <Button
              onClick={handlePublishSelected}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Публикувай ({selectedForPublish.length})
            </Button>
            <Button
              onClick={handleUnpublishSelected}
              disabled={saving}
              variant="outline"
            >
              Върни в чернова
            </Button>
          </div>
        )}
      </div>

      {/* Content Blocks by Section */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
        </div>
      ) : Object.keys(groupedBlocks).length === 0 ? (
        <div className="text-center py-20 bg-primary-charcoal rounded-lg border border-neutral-700">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
          <p className="text-neutral-400">Няма намерено съдържание</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedBlocks).map(([section, sectionBlocks]) => (
            <div key={section} className="bg-primary-charcoal rounded-lg border border-neutral-700 overflow-hidden">
              <div className="bg-primary-black px-6 py-3 border-b border-neutral-700">
                <h2 className="text-lg font-semibold capitalize">{section}</h2>
              </div>
              
              <div className="divide-y divide-neutral-700">
                {sectionBlocks.map(block => {
                  const editBlock = editingBlocks[block._id];
                  const hasChanges = editBlock?.value !== block.value;
                  
                  return (
                    <div key={block._id} className="p-6 hover:bg-primary-black/30 transition-colors">
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedForPublish.includes(block._id)}
                          onChange={() => toggleSelection(block._id)}
                          className="mt-3 w-4 h-4 rounded border-neutral-600 bg-primary-black"
                        />

                        {/* Content */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-neutral-200">{block.key}</h3>
                              <p className="text-sm text-neutral-500 font-mono">{block.section}</p>
                            </div>
                            {getStatusBadge(block.status)}
                          </div>

                          <textarea
                            value={editBlock?.value || ''}
                            onChange={(e) => handleValueChange(block._id, e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 bg-primary-black border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-y"
                          />

                          {hasChanges && (
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-yellow-400">Има незапазени промени</p>
                              <Button
                                size="sm"
                                onClick={() => handleSaveBlock(block._id)}
                                disabled={saving}
                                className="bg-accent-gold hover:bg-accent-gold-dark"
                              >
                                <Save className="w-3 h-3 mr-2" />
                                Запази
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-200">
            <p className="font-medium mb-1">Как работи публикуването?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-300">
              <li>Променете текста и запазете като чернова</li>
              <li>Изберете блоковете с checkboxes</li>
              <li>Натиснете "Публикувай" - промените ще се видят на публичния сайт веднага</li>
              <li>Черновките не се показват на публичния сайт</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
